import React, { useMemo, useState } from 'react';
import { getIn } from 'formik';
import './button.scss';

export enum ActionType {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
}

export enum ColorScheme {
  LIGHT = 'light',
  DARK = 'dark',
}

export enum Height {
  SMALL = 'small-size',
  MEDIUM = 'medium-size',
  LARGE = 'large-size',
}

export enum Responsiveness {
  NONE = 'responsiveness-none',
  SMALL_SIZE_ON_MOBILE_AND_TABLET = 'responsive-small-size-on-mobile-and-tablet',
}

export type ButtonStyle = {
  actionType: ActionType;
  colorScheme: ColorScheme;
  height: Height;
  responsiveness: Responsiveness;
};

export function DefaultButtonStyle() {
  return new ButtonStyleBuilder();
}

class ButtonStyleBuilder {
  buttonStyle: ButtonStyle;

  constructor(buttonStyle?: ButtonStyle) {
    if (buttonStyle !== undefined) {
      this.buttonStyle = buttonStyle;
    } else {
      this.buttonStyle = {
        actionType: ActionType.PRIMARY,
        colorScheme: ColorScheme.DARK,
        height: Height.MEDIUM,
        responsiveness: Responsiveness.NONE,
      };
    }
  }

  hasPrimaryAction() {
    this.buttonStyle.actionType = ActionType.PRIMARY;
    return this;
  }

  hasSecondaryAction() {
    this.buttonStyle.actionType = ActionType.SECONDARY;
    return this;
  }

  hasDarkMode() {
    this.buttonStyle.colorScheme = ColorScheme.DARK;
    return this;
  }

  hasLightMode() {
    this.buttonStyle.colorScheme = ColorScheme.LIGHT;
    return this;
  }

  isSmallSize() {
    this.buttonStyle.height = Height.SMALL;
    return this;
  }

  isMediumSize() {
    this.buttonStyle.height = Height.MEDIUM;
    return this;
  }

  isLargeSize() {
    this.buttonStyle.height = Height.LARGE;
    return this;
  }

  isResponsive() {
    this.buttonStyle.responsiveness = Responsiveness.SMALL_SIZE_ON_MOBILE_AND_TABLET;
    return this;
  }

  build() {
    return this.buttonStyle;
  }
}

export enum ButtonParamType {
  NormalButton = 'NormalButton',
  CheckboxButton = 'CheckboxButton',
  RadioButton = 'RadioButton',
}

export type NormalButton = {
  type: ButtonParamType.NormalButton;
  onClick: () => void;
};

export type CheckboxButton = {
  type: ButtonParamType.CheckboxButton;
  name: string;
};

export type RadioButton = {
  type: ButtonParamType.RadioButton;
  value: string;
};

export type ButtonParams = NormalButton | CheckboxButton | RadioButton;

export function Button(props: {
  buttonStyle: ButtonStyle;
  children: React.ReactFragment;
  buttonParams: ButtonParams;
  [rest: string]: any;
  className?: string;
  width?: number;
  field?;
  form?;
}) {
  const [buttonStyle] = useState(getButtonStyleString(props.buttonStyle));

  const disabled = useMemo(
    (): boolean =>
      props.disabled ||
      (props.buttonParams.type === ButtonParamType.RadioButton &&
        (getIn(props.form?.initialStatus?.initialDisabled || {}, props.field.name) ||
          props.form?.initialStatus?.initialDisabled?.[props.field.name])),
    [
      props.disabled,
      props.buttonParams.type,
      props.field?.name,
      props.form?.initialStatus?.initialDisabled,
    ],
  );

  function getButtonStyleString(buttonStyle: ButtonStyle) {
    const classNames: Array<string> = [];
    classNames.push(buttonStyle.actionType);
    classNames.push(buttonStyle.colorScheme);
    classNames.push(buttonStyle.height);
    classNames.push(buttonStyle.responsiveness);
    return classNames.join(' ');
  }

  switch (props.buttonParams.type) {
    case ButtonParamType.RadioButton:
      if (!props?.field?.name) {
        throw new Error('Button component: Radio button requires name field to be specified');
      }
      break;
    case ButtonParamType.CheckboxButton:
      if (props?.field?.name) {
        throw new Error('Button component: Checkbox button will ignore name field');
      }
      break;
  }

  const onClick = () => {
    switch (props.buttonParams.type) {
      case ButtonParamType.NormalButton:
        props.buttonParams.onClick();
        break;
      case ButtonParamType.CheckboxButton:
        if (props.form && props.field) {
          const isValueChecked = !!props.form.values[props.buttonParams.name];
          props.form.setFieldValue(props.buttonParams.name, !isValueChecked);
        }
        break;
      case ButtonParamType.RadioButton:
        if (props.form && props.field) {
          props.form.setFieldValue(props.field.name, props.buttonParams.value);
        }
        break;
    }
  };

  const isChecked = () => {
    switch (props.buttonParams.type) {
      case ButtonParamType.CheckboxButton:
        return !!props.form.values[props.buttonParams.name];
      case ButtonParamType.RadioButton:
        return props?.field?.value === props.buttonParams.value;

      default:
        return false;
    }
  };

  return (
    <div className="Button">
      <button
        type="button"
        disabled={disabled}
        {...props.rest}
        onClick={onClick}
        className={`${props.className ? props.className : ''} ${buttonStyle} ${
          isChecked() ? 'checked' : ''
        }`}
      >
        {props.children}
      </button>
    </div>
  );
}
